import { Fragment } from 'react';
import type { SalesFunnelStage } from '@/types/sales-performance.types';
import { count, percent } from './format';

/** Figma Kim-AI 6717:60228 — the funnel drawn as one bar per stage. */

/** Track padding (2px each side) plus the 6px gap before the drop segment. */
const TRACK_INSET = 10;

export function PerformanceBarList({ stages }: { stages: SalesFunnelStage[] }) {
  const maximum = stages[0]?.count || 1;
  const biggestDrop = Math.max(0, ...stages.slice(1).map((stage) => stage.dropOff));
  return <div className='flex w-full flex-col gap-3'>
    {stages.map((stage, index) => {
      const ratio = Math.min(1, stage.count / maximum);
      const hasDrop = index > 0 && stage.dropOff > 0;
      // With a drop segment the retained bar shares the track; without one it fills its share alone.
      const barWidth = hasDrop ? `calc((100% - 6px) * ${ratio})` : `${ratio * 100}%`;
      const labelWidth = `calc((100% - ${TRACK_INSET}px) * ${ratio} + 2px)`;
      return <Fragment key={stage.key}>
        {index > 0 && <hr aria-hidden className='h-px w-full border-0 bg-gray-300' />}
        <div className='flex w-full items-center gap-12 overflow-clip'>
          <div className='flex w-[150px] shrink-0 flex-col gap-0.5'>
            <span className='text-sm font-medium leading-5 text-ink'>{stage.label}</span>
            <span className='flex items-center gap-1 whitespace-nowrap'>
              <span className='text-[13px] leading-[18px] text-ink-strong'>{count(stage.count)}</span>
              <span className='text-xs leading-4 text-gray-900'>/ {percent(stage.count / maximum * 100)}</span>
            </span>
          </div>
          <div className='flex min-w-0 flex-1 flex-col gap-[3px]'>
            <div className='flex items-start gap-1.5 text-xs leading-4 text-gray-800'>
              <span className='shrink-0' style={{ width: hasDrop ? labelWidth : undefined }}>{count(stage.count)}</span>
              {hasDrop && <span className='shrink-0'>{count(stage.dropOff)}</span>}
            </div>
            <div className='flex h-5 w-full items-center gap-1.5 rounded-full bg-gray-100 p-0.5' aria-label={`${stage.label}: ${stage.count}; ${stage.dropOffLabel ?? 'drop-off'}: ${stage.dropOff}`}>
              <div className='h-full shrink-0 rounded-[10px] bg-indigo-500' style={{ width: barWidth }} />
              {hasDrop && <div className={`h-full min-w-px flex-1 rounded-[10px] ${stage.dropOff === biggestDrop ? 'bg-red-200' : 'bg-gray-400'}`} />}
            </div>
          </div>
        </div>
      </Fragment>;
    })}
  </div>;
}
